import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { SharedUtilsService } from '@app/services';

@Component({
    selector: 'gmail-auth-code',
    templateUrl: './gmail-auth-code.component.html',
    styleUrls: ['./gmail-auth-code.component.scss'],
    standalone: false
})
export class GmailAuthCodeComponent implements OnInit, OnDestroy {
  authCode: string;
  codeSent: boolean = false;
  // Set once the auto-close attempt has come and gone without the window
  // actually closing — used to reveal the code + manual-close note instead of
  // leaving the user on a misleading "this window will close automatically...".
  autoCloseFailed: boolean = false;
  // Populated when Google redirects with ?error=... — shown in the popup UI so the user knows why.
  errorMessage: string = null;

  private broadcastChannel: BroadcastChannel | null = null;
  private repostTimer: any;
  private stopRepostTimer: any;

  constructor(
    private activatedRoute: ActivatedRoute,
    private _sharedService: SharedUtilsService
  ) {
    this.activatedRoute.queryParams.subscribe(params => {
      this.authCode = params['code'] ?? null;

      const rawError = params['error'] ?? null;
      const rawErrorDescription = params['error_description'] ?? null;

      if (this.authCode) {
        this.sendCodeBack();
      } else if (rawError) {
        this.errorMessage = this.formatOAuthError(String(rawError), rawErrorDescription);
      }
    });
  }

  ngOnInit() { }

  ngOnDestroy() {
    this.cleanup();
  }

  sendCodeBack() {
    // Clear any stale error state (defensive - subscribe already guards, but future callers should get a clean reset).
    this.errorMessage = null;

    const payload = { type: 'gmail-auth-code', code: this.authCode };

    try {
      this.broadcastChannel = new BroadcastChannel('gmail-auth');
    } catch (e) {
      // BroadcastChannel not supported
    }

    // Deliver the code on every channel we have. window.opener.postMessage and
    // BroadcastChannel can BOTH silently fail to reach the opener (popup opened
    // without an opener, or BroadcastChannel partitioned across browsing
    // contexts), so we also hand off via localStorage — writing it fires a
    // reliable 'storage' event in every other same-origin tab. The opener
    // dedupes on receipt, so posting repeatedly is harmless.
    const post = () => {
      try { this.broadcastChannel?.postMessage(payload); } catch (e) { /* no-op */ }

      if (window.opener) {
        try { window.opener.postMessage(payload, window.location.origin); } catch (e) { /* no-op */ }
      }

      try {
        // Vary the value each post (ts) so the storage event always fires —
        // re-writing an identical value is a no-op that wouldn't notify the opener.
        localStorage.setItem('gmail-auth-code', JSON.stringify({ code: this.authCode, ts: Date.now() }));
      } catch (e) { /* no-op */ }

      this.codeSent = true;
    };

    post();
    // Re-post briefly so an opener that attaches its listener a beat late (or a
    // backgrounded tab that wakes up) still receives the code.
    this.repostTimer = setInterval(post, 600);
    this.stopRepostTimer = setTimeout(() => clearInterval(this.repostTimer), 4000);

    // Attempt to auto-close. window.close() is a silent no-op when the browser
    // refuses it (tab wasn't script-opened, or the OAuth redirect severed the
    // opener), so we can't rely on it. If we're still running shortly after the
    // attempt, the close was blocked — reveal the code + manual-close note so
    // the user is never stranded.
    setTimeout(() => {
      window.close();
      setTimeout(() => { this.autoCloseFailed = true; }, 400);
    }, 1500);
  }

  cleanup() {
    clearInterval(this.repostTimer);
    clearTimeout(this.stopRepostTimer);
    try { this.broadcastChannel?.close(); } catch (e) { /* no-op */ }
    this.broadcastChannel = null;
  }

  showConfirmCopy() {
    this._sharedService.showNotification("Authentication Code Copied to Clipboard");
  }


  // Turn Google's OAuth error code (and optional description) into a friendly single-line message.
  // Known codes get their own tailored text; unknown codes fall back to whatever Google sent us.
  formatOAuthError(errorCode: string, description?: any): string {
    const knownMessages: Record<string, string> = {
      'access_denied': 'Access was denied. You may have declined the Google prompt, or Google blocked the request.',
      'admin_policy_enforced': 'Your Google workspace admin has restricted this integration.',
      'disallowed_useragent': 'Google refused to authorize inside this browser environment.',
      'org_internal': 'This OAuth client is restricted to a different Google organization.',
      'invalid_client': 'The OAuth client configuration is invalid on the server side.',
      'deleted_client': 'The OAuth client has been deleted or disabled.',
      'redirect_uri_mismatch': 'The redirect URL does not match what Google expects. Server config issue.',
      'invalid_request': 'The request Google received was malformed.',
    };

    // Known code - use the tailored message alone; skip description to avoid saying the same thing twice.
    const friendly = knownMessages?.[errorCode];
    if (friendly) return friendly;

    // Unknown code - combine everything we have so we don't lose signal for future debugging.
    const parts: string[] = [];
    if (errorCode) parts.push(`Google returned an error: ${errorCode}`);
    if (description) parts.push(String(description));
    if (!parts.length) parts.push('Google denied the request but did not tell us why.');
    return parts.join(' ');
  }
}
